import { Buffer } from 'buffer';

import { KeyValue, KeyValuePipe, NgClass } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, NgZone, OnDestroy, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard, MatCardActions, MatCardTitle } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
  CommonIPStateTabType,
  DEFAULT_IP_TYPES_UPLOAD_BAR,
  DOC_ACTION_TYPES,
  DocumentSubType,
  EpsWsWrapper,
  IP,
  IP_TYPE_DEFAULTS,
  IpTypeLabel,
  TRIFOLD_MAP_DEFAULTS,
  TrifoldDetailMap,
  UploadSubTypeGroupingMode,
} from '@model/ftf-api.model';
import { Store } from '@ngrx/store';
import { AccountService } from '@service/account.service';
import { AddinContextService } from '@service/addin-context.service';
import { AddinOauthService } from '@service/addin-oauth-service.service';
import { DialogService } from '@service/dialog.service';
import { LoaderService } from '@service/loader.service';
import { LoggerService } from '@service/logger.service';
import { BASE_URL_REGEX, SettingsService, parseBaseUrl } from '@service/settings.service';
import { selectDocumentSubTypes } from '@store/selectors/document-subtypes.selectors';
import { convertFileToBuffer, formatDateTimeYYYYMMDDHHMMSS } from '@utils/file-utils';
import { Observable, Subject, catchError, delay, finalize, map, merge, of, switchMap, takeUntil } from 'rxjs';

import { IpIconComponent } from '../../shared/components/ip-icon/ip-icon.component';
import { RegExpDialogComponent } from './reg-exp-dialog/reg-exp-dialog.component';
import { SortingComponent } from './sort-setting/sort-setting.component';

interface DetailTabOptions {
  key: CommonIPStateTabType;
  value: string;
  ipTypes: IP.TrifTypeEnum[];
}

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  imports: [
    MatCard,
    MatIconButton,
    MatIcon,
    MatCardTitle,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    ReactiveFormsModule,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    NgClass,
    MatFormField,
    MatLabel,
    MatInput,
    MatHint,
    MatError,
    MatSelect,
    MatSelectTrigger,
    MatTooltip,
    IpIconComponent,
    MatOption,
    MatCheckbox,
    SortingComponent,
    MatCardActions,
    MatButton,
    KeyValuePipe,
  ],
})
export class SettingsComponent implements OnDestroy {
  private accountService = inject(AccountService);
  private addinContext = inject(AddinContextService);
  private dialogService = inject(DialogService);
  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private loader = inject(LoaderService);
  private loggerService = inject(LoggerService);
  private matDialog = inject(MatDialog);
  private ngZone = inject(NgZone);
  private oauthService = inject(AddinOauthService);
  private router = inject(Router);
  private settingsService = inject(SettingsService);
  private snackBar = inject(MatSnackBar);
  private store = inject(Store);

  /**
   *  The ngDestroyed will be used to kill connections
   * to the store when a component is destroyed
   */
  protected $ngDestroyed = new Subject<void>();
  form: FormGroup;
  ipTypes: IpTypeLabel<IP.TrifTypeEnum>[] = IP_TYPE_DEFAULTS;
  trifoldMap: TrifoldDetailMap = TRIFOLD_MAP_DEFAULTS;
  documentActions = DOC_ACTION_TYPES;
  patentDocumentSubtypes: DocumentSubType[] = [];
  trademarkDocumentSubtypes: DocumentSubType[] = [];
  matterDocumentSubtypes: DocumentSubType[] = [];
  selectedPatentSubtype?: DocumentSubType;
  selectedTrademarkSubtype?: DocumentSubType;
  selectedMatterSubtype?: DocumentSubType;
  defaultIpTypesUploadBar = DEFAULT_IP_TYPES_UPLOAD_BAR;
  uploadSubTypeGroupingMode = UploadSubTypeGroupingMode;
  defaultViewOptions = [
    { key: 'detail', value: 'Case Details View' },
    { key: 'trifold', value: 'Trifold View' },
  ];

  // Base options for all IP types
  private detailTabOptions: DetailTabOptions[] = [
    { key: 'biblio', value: 'Details', ipTypes: ['Patent', 'Trademark', 'Matter'] },
    { key: 'notes', value: 'Case Notes', ipTypes: ['Patent', 'Trademark', 'Matter'] },
    { key: 'references', value: 'References', ipTypes: ['Patent'] },
    { key: 'workflow', value: 'Workflows', ipTypes: ['Patent', 'Trademark', 'Matter'] },
  ];

  // Filter options for each IP type

  /**
   * Gets the default tab options for the patent IP type.
   *
   * @readonly
   * @returns DetailTabOptions[]
   */
  get patentDefaultTabOptions() {
    return this.detailTabOptions.filter((option) => option.ipTypes.includes('Patent'));
  }

  /**
   * Gets the default tab options for the trademark IP type.
   *
   * @readonly
   * @returns DetailTabOptions[]
   */
  get trademarkDefaultTabOptions() {
    return this.detailTabOptions.filter((option) => option.ipTypes.includes('Trademark'));
  }

  /**
   * Gets the default tab options for the matter IP type.
   *
   * @readonly
   * @returns DetailTabOptions[]
   */
  get matterDefaultTabOptions() {
    return this.detailTabOptions.filter((option) => option.ipTypes.includes('Matter'));
  }

  /**
   * Constructor for the SettingsComponent.
   *
   * Initializes the settings form and subscribes to account details loading.
   */
  constructor() {
    const { settingsService } = this;
    const { fb } = this;

    const docSubTypesRefreshed$ = this.store.select(selectDocumentSubTypes).pipe(
      takeUntil(this.$ngDestroyed),
      map((subtypes) => {
        this.patentDocumentSubtypes = subtypes.filter((s) => s.usedInPatent).sort((a, b) => a.name!.localeCompare(b.name!));
        this.trademarkDocumentSubtypes = subtypes.filter((s) => s.usedInTm).sort((a, b) => a.name!.localeCompare(b.name!));
        this.matterDocumentSubtypes = subtypes.filter((s) => s.usedInOm).sort((a, b) => a.name!.localeCompare(b.name!));
      })
    );

    // THIS FORM MUST MATCH the AddinSettings interface and values of "default" property object in settings.service.ts
    this.form = fb.group({
      baseUrl: [null, [Validators.pattern(BASE_URL_REGEX), Validators.required], [this.validateBaseUrl.bind(this)]],
      regExp: [null],
      regExpActive: [false],
      appNumberMatchingEnabled: [false],
      ipTypeDefault: ['All'],
      autoRenameDuplicateFiles: [false],
      navigationBarUploadOption: [false],
      markAllAttachmentsForUpload: [true],
      markAllEmbeddedImagesForUpload: [true],
      includeFileExtension: [false],
      markAsUrgent: [false],
      autoTruncateFileName: [false],
      autoTruncateTitleName: [false],
      trifoldPatentDefault: [IP.TrifoldEnum.L],
      trifoldMatterDefault: [IP.TrifoldEnum.L],
      trifoldTrademarkDefault: [IP.TrifoldEnum.L],
      trifoldDocumentIconAction: ['3'],
      trifoldDocumentTextAction: ['2'],
      defaultIpTypeUploadBar: ['1'],
      uploadSubTypeGroupingMode: [UploadSubTypeGroupingMode.DocSubType],
      patentParentSubType: [''],
      patentAttachSubType: [''],
      patentDefaultView: ['trifold'],
      patentDefaultTab: ['biblio'],
      trademarkParentSubType: [''],
      trademarkAttachSubType: [''],
      trademarkDefaultView: ['trifold'],
      trademarkDefaultTab: ['biblio'],
      matterParentSubType: [''],
      matterAttachSubType: [''],
      patentSortSettings: [],
      trademarkSortSettings: [],
      matterSortSettings: [],
      matterDefaultView: ['trifold'],
      matterDefaultTab: ['biblio'],
    });
    this.createSubtypeSelectionObservable('patentParentSubType', docSubTypesRefreshed$).subscribe((id) => {
      this.selectedPatentSubtype = this.patentDocumentSubtypes.find((dt) => dt.id === id);
    });
    this.createSubtypeSelectionObservable('trademarkParentSubType', docSubTypesRefreshed$).subscribe((id) => {
      this.selectedTrademarkSubtype = this.trademarkDocumentSubtypes.find((dt) => dt.id === id);
    });
    this.createSubtypeSelectionObservable('matterParentSubType', docSubTypesRefreshed$).subscribe((id) => {
      this.selectedMatterSubtype = this.matterDocumentSubtypes.find((dt) => dt.id === id);
    });
    this.form.patchValue(settingsService.getSettings());
    this.form.markAsUntouched();
    this.accountService.isAccountDetailsLoaded$.pipe(takeUntil(this.$ngDestroyed)).subscribe((loaded) => {
      if (!loaded) return;
      this.ngZone.run(() => {
        // update the custom labels of setting screen
        // once setting service actually has any
        this.ipTypes = this.accountService.trifIpTypes;
        this.trifoldMap = this.accountService.trifoldMap;
      });
    });
  }

  /**
   * Gets the flag indicating whether the add-in is running in Outlook or not in an Office application (Widget / Browser).
   *
   * @readonly
   * @type {boolean}
   */
  get isOutlookOrNotOffice(): boolean {
    return this.addinContext.isOutlook || !this.addinContext.isOfficeActive;
  }

  /**
   * Compares two key-value pairs for ascending order.
   * @param a The first key-value pair.
   * @param b The second key-value pair.
   * @returns A negative number if a < b, a positive number if a > b, and 0 if they are equal.
   */
  valueAscOrder = (a: KeyValue<string, string>, b: KeyValue<string, string>): number => {
    return a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: 'base' });
  };

  /**
   * Opens the regular expression dialog.
   * @returns False to prevent default behavior.
   */
  openRegExpDialog(): boolean {
    this.matDialog.open(RegExpDialogComponent, {
      width: '300px',
      maxWidth: '85vw',
      data: {
        title: 'Case Number Matching',
        regexpHint: 'Regular expression is valid',
        caseNameHint: 'Enter a test case name',
        button1: {
          text: 'Test',
          style: { color: 'basic' },
        },
        button2: {
          text: 'Default',
          style: { color: 'basic' },
        },
        button3: {
          text: 'Save',
          style: { color: 'primary' },
        },
      },
    });
    return false;
  }

  /**
   * Saves the current settings to the settings service and navigates to the home page.
   */
  private save() {
    const settings = this.settingsService.getSettings();
    this.form.patchValue({ regExp: settings.regExp });
    this.settingsService.setSettings(this.form.getRawValue());
    this.router.navigate(['taskpane', 'home']);
  }

  /**
   * Resets all settings to their default values.
   */
  private reset() {
    this.settingsService.resetSettings();
    this.form.patchValue(this.settingsService.getSettings());
    this.form.markAsUntouched();

    this.snackBar.open('Successfully reset settings.', 'Dismiss', {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      politeness: 'assertive',
      duration: 5000,
    });
  }

  private createSubtypeSelectionObservable(formControlName: string, arrayRefresh$: Observable<void>) {
    return merge(arrayRefresh$, this.form.get(formControlName)!.valueChanges).pipe(
      takeUntil(this.$ngDestroyed),
      map(() => this.form.get(formControlName)!.value)
    );
  }

  /**
   * Checks if the IP type should be shown.
   * @returns True if the IP type should be shown, false otherwise.
   */
  showIpType(): boolean {
    return this.settingsService.getBaseUrl() !== '';
  }

  /**
   * Gets the filtered IP types based on the form value.
   * @returns The filtered IP types.
   */
  getFilteredIpTypes() {
    if ('All' === this.form.get('ipTypeDefault')?.value) return 'All';
    const filtered = this.ipTypes.find((type) => type.key === this.form.get('ipTypeDefault')?.value);
    return filtered ? filtered.value : '';
  }

  /**
   * Validates the base URL.
   * @param control The form control to validate.
   * @returns An observable that emits validation errors, if any.
   */
  validateBaseUrl(control: AbstractControl) {
    return of(control.value).pipe(
      delay(500),
      switchMap((candidate) => {
        this.loader.show();
        const baseUrl = parseBaseUrl(candidate);
        if (baseUrl === '') return of({ notApi: true });
        const api = `${baseUrl}api/json/version/`;
        return this.http.get<EpsWsWrapper<string>>(api, { observe: 'response' }).pipe(
          switchMap((resp) => (resp.status === 200 && resp.body?.epsws?.data ? of(null) : of({ notApi: true }))),
          catchError(() => {
            return of({ notApi: true });
          }),
          finalize(() => {
            this.loader.hide();
          })
        );
      }),
      catchError(() => {
        return of({ notApi: true });
      })
    );
  }

  /**
   * Parses the base URL from the form.
   * @returns
   */
  parsedUrl() {
    return parseBaseUrl(this.form.get('baseUrl')?.value);
  }

  /**
   * Handles the reset click event.
   */
  onResetClick() {
    this.dialogService.openDialog({
      width: '400px',
      title: 'Reset All Settings',
      message:
        'This will reset ALL settings to their defaults. If you use the add-in across multiple applications, settings in each may be affected. Custom case-matching regular expressions will also be reset. Proceed with reset?',
      button1: {
        text: 'Cancel',
        style: { color: 'basic' },
      },
      button2: {
        text: 'Reset',
        style: { color: 'warn' },
        action: () => {
          this.reset();
        },
      },
    });
  }

  /**
   * Handles the save click event.
   */
  onSaveClick() {
    this.save();
  }

  /**
   * Handles the backup click event.
   */
  onBackupClick() {
    const base64 = Buffer.from(JSON.stringify(this.settingsService.getSettings())).toString('base64');
    const a = document.createElement('a');
    a.href = `data:text/plain;base64,${base64}`;
    a.download = `ftf-addin-settings.${formatDateTimeYYYYMMDDHHMMSS(new Date()).replace(/[:]/g, '-')}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  }

  /**
   * Handles the file browse event.
   * @param event The file browse event.
   */
  onBrowseFiles(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files ? Array.from(target.files) : [];
    target.value = '';

    // have to delay it otherwise file api will keep popup from opening
    setTimeout(() => {
      if (files.length === 0) return;
      convertFileToBuffer(files[0]).subscribe((buffer) => {
        try {
          const contents = buffer.toString();
          if (!/^{.*?"baseUrl":.*}$/s.test(contents.trim())) {
            throw new Error('Invalid settings file');
          }
          const settings = JSON.parse(contents);
          this.settingsService.setSettings(settings);
          this.form.patchValue(this.settingsService.getSettings());
          this.form.markAsUntouched();
        } catch (ex) {
          this.loggerService.error('Error parsing settings file', ex);
          this.snackBar.open('Problems restoring settings from file.', 'Dismiss', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            politeness: 'assertive',
            duration: 5000,
          });
        }
      });
    }, 100);
  }

  /**
   * Handles the back button click event.
   */
  onBackButtonClick() {
    if (!this.oauthService.hasValidAccessToken()) {
      this.loggerService.info('[SettingsComponent] User is not logged in, redirecting to login page');
      this.router.navigateByUrl('/taskpane/login');
    } else {
      this.loggerService.info('[SettingsComponent] User is logged in, redirecting to home page');
      this.router.navigateByUrl('/taskpane/home');
    }
  }

  /**
   * Gets the keys of the trifold map.
   * @returns An array of keys for the trifold map.
   */
  getTrifoldMapKeys(): IP.TrifTypeEnum[] {
    return Object.keys(this.trifoldMap) as IP.TrifTypeEnum[];
  }

  /**
   * Gets the keys of the trifold enum for a specific key.
   * @param key The key to look up in the trifold map.
   * @returns An array of string keys for the specified trifold enum.
   */
  getTrifoldEnumKeys(key: string): string[] {
    if (key in this.trifoldMap) {
      const entry = this.trifoldMap[key as keyof typeof this.trifoldMap];
      return Object.values(entry).map((value) => value.label);
    }
    return [];
  }

  /**
   * Kills connections to various observables
   * destroys the component
   */
  ngOnDestroy() {
    this.$ngDestroyed.next();
    this.$ngDestroyed.complete();
  }
}
